import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { socialLinks } from '../data/homeContent';
import { trackEvent, analyticsEvents, contactInfo } from '../data/mock';
import { LOGO_LIGHT_URL } from '../data/brandAssets';
import CookieSettingsModal from './CookieSettingsModal';

const columns = [
  {
    title: 'Fläche verkaufen',
    links: [
      ['Ackerland verkaufen', '/ackerland-verkaufen'], ['Wald verkaufen', '/wald-verkaufen'], ['Grünland verkaufen', '/gruenland-verkaufen'],
      ['Bauernhöfe & Hofstellen', '/bauernhoefe-verkaufen'], ['Eigenjagd', '/eigenjagd-verkaufen'], ['Sonderkulturen', '/sonderkulturen-verkaufen'],
      ['Weinberge', '/weinberge-verkaufen'], ['Ökopunkte', '/oekopunkte-verkaufen'], ['Verkaufsprozess', '/verkaufsprozess'], ['Bieterverfahren', '/bieterverfahren']
    ]
  },
  {
    title: 'Fläche kaufen',
    links: [
      ['Aktuelle Immobilien', '/immobilien'], ['Suchprofil anlegen', '/suchprofil'], ['Käufer-Radar', '/kaeufer-radar'], ['Meine Gebote', '/meine-gebote'],
      ['Nachlass-Planer', '/nachlass-planer'], ['Aktuelle Preise', '/agrarflaechen-preise'], ['WhatsApp-Kontakt', contactInfo.whatsappLink], ['WhatsApp-Kanal abonnieren', 'https://whatsapp.com/channel/0029VbAjAglATRSetGD53E0J']
    ]
  },
  {
    title: 'AgrarKapital',
    links: [
      ['Über uns', '/warum-agrarkapital'], ['Unsere Leistungen', '/leistungen'], ['Einzugsgebiet', '/einzugsgebiet'],
      ['Ratgeber', '/ratgeber'], ['Lexikon', '/ratgeber/lexikon'], ['Kontakt', '/kontakt']
    ]
  }
];

const Footer = () => {
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const social = (platform, url) => { trackEvent(analyticsEvents.SOCIAL_CLICK, { platform, location: 'footer' }); window.open(url, '_blank', 'noopener'); };

  return (
    <footer className="hv-footer bg-[#0F2E22] text-[#E7EBE4]" data-testid="site-footer">
      <div className="container mx-auto px-4 py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <img src={LOGO_LIGHT_URL} alt="AgrarKapital" className="h-12 w-auto mb-5" />
            <p className="text-[15px] leading-relaxed text-[#C9D3C8] max-w-[36ch]">
              Fachmakler für Ackerland, Wald, Grünland und Bauernhöfe. Persönlich geführt von Andreas Heinrichs, Freising – tätig in ganz Deutschland.
            </p>
            <ul className="mt-6 space-y-3 text-[15px]">
              <li><a href={contactInfo.phoneLink} data-testid="footer-phone" className="inline-flex items-center gap-3 hover:text-[#DFC590]"><Phone className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />{contactInfo.phone}</a></li>
              <li><a href={contactInfo.emailLink} data-testid="footer-email" className="inline-flex items-center gap-3 hover:text-[#DFC590]"><Mail className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />{contactInfo.email}</a></li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#DFC590] mt-1" aria-hidden="true" /><span>Rindermarkt 8<br />85354 Freising</span></li>
            </ul>
            <div className="flex flex-wrap gap-2 mt-6">
              {socialLinks.map((sl) => (
                <button key={sl.key} type="button" onClick={() => social(sl.key, sl.url)} data-testid={`footer-${sl.key}`} className="hv-icon-btn hv-icon-btn-light !w-auto px-3 gap-2 text-sm">
                  <sl.icon className="w-4 h-4" aria-hidden="true" />{sl.label}
                </button>
              ))}
            </div>
          </div>
          {columns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#DFC590] mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={to}>{to.startsWith('http') ? <a href={to} target="_blank" rel="noopener noreferrer" className="text-[15px] text-[#C9D3C8] hover:text-[#F8F6F0]">{label}</a> : <Link to={to} className="text-[15px] text-[#C9D3C8] hover:text-[#F8F6F0]">{label}</Link>}</li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#DFC590] mb-4">Bewertung</h3>
            <p className="text-[15px] text-[#C9D3C8] mb-4 max-w-[30ch]">Kostenlos, unverbindlich, Rückmeldung innerhalb von 24 Stunden.</p>
            <Link to="/bewertung" data-testid="footer-cta" className="hv-btn-accent hv-footer-cta inline-flex items-center justify-center h-[48px] px-5 rounded-[4px] bg-[#DFC590] text-[#19392B] font-semibold hover:bg-[#E8D3A9]">Bewertung anfordern</Link>
          </div>
        </div>

        <div className="border-t border-[#2A5A45] mt-14 pt-6 flex flex-col sm:flex-row gap-4 justify-between text-sm text-[#A9B8A9]">
          <p>&copy; {new Date().getFullYear()} AgrarKapital · Heinrichs Immobilien</p>
          <div className="hv-footer-legal flex flex-wrap items-baseline gap-x-5 gap-y-2">
            <Link to="/impressum" className="hover:text-[#F8F6F0]">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#F8F6F0]">Datenschutz</Link>
            <Link to="/widerruf" className="hover:text-[#F8F6F0]">Widerruf</Link>
            <button type="button" onClick={() => setIsCookieModalOpen(true)} data-testid="footer-cookie-settings" className="hover:text-[#F8F6F0]">Cookie-Einstellungen</button>
          </div>
        </div>
      </div>
      <CookieSettingsModal isOpen={isCookieModalOpen} onClose={() => setIsCookieModalOpen(false)} />
    </footer>
  );
};

export default Footer;
